import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import {
    apiQuery,
    getSiteSpacesFromStructure,
    useCurrentOrganizationId,
    useRealtimeChannel,
} from '../../state';
import { type InitiatedGitSync, useGitSyncCurrentTarget } from './GitSyncTargetStatusProvider';

export type GitSyncCurrentStatus =
    | {
          state: 'running';
          direction: api.GitSyncOperationDirection;
          stage: api.GitSyncOperationStage;
          error?: undefined;
      }
    | {
          state: 'failure';
          direction: api.GitSyncOperationDirection;
          stage?: undefined;
          /** Undefined when the operation recorded no message for the failure. */
          error?: string;
          errorAction?: api.GitSyncOperationErrorAction;
      }
    | {
          state: 'timed-out';
          direction: api.GitSyncOperationDirection;
          stage?: undefined;
          error?: undefined;
      }
    | {
          state: 'success';
          direction: api.GitSyncOperationDirection;
          stage?: undefined;
          error?: undefined;
      };

export type GitSyncCurrentOperation = Pick<
    api.GitSyncSpaceOperation,
    'state' | 'direction' | 'startedAt'
> &
    Partial<Pick<api.GitSyncSpaceOperation, 'stage' | 'error' | 'errorAction'>>;

export type GitSyncOperationSource =
    | { type: 'space'; spaceId: string }
    | { type: 'site'; site: api.Site };

export type GitSyncCurrentStatusResult = {
    /** Canonical Status to use for badges and other summary surfaces. Aggregated for a site target. */
    status: GitSyncCurrentStatus | null;
    /** The site's own operation status. Only present for a site target. */
    site?: GitSyncCurrentStatus | null;
    /** Individual managed-space statuses keyed by space ID. Only present for a site target. */
    spaces?: Record<string, GitSyncCurrentStatus | null>;
};

/**
 * Whether a Git Sync status represents a terminal failure — a failed operation or one that timed
 * out. Single source of truth for the "sync failed" check shared across the status surfaces.
 */
export function isFailedGitSyncStatus(status: GitSyncCurrentStatus | null): boolean {
    return status?.state === 'failure' || status?.state === 'timed-out';
}

/**
 * Return the current status of the GitSync installation.
 *
 * The realtime operation is preferred once it arrives — it alone carries the stage and the failure
 * message — but the target decides whether an operation is still running, since the
 * channel replays its last message and never publishes one for an operation whose worker died.
 * Until the channel arrives the status falls back to the target entirely, so a status is reported
 * from the very first render. A sync the user just triggered under a
 * {@link GitSyncTargetStatusProvider} takes precedence over both.
 *
 * A space reports only its own status. A site reports the aggregate status of its own operation and
 * all managed spaces from the same site channel.
 */
export function useGitSyncCurrentStatus(target: api.Space | api.Site): GitSyncCurrentStatusResult {
    const currentTarget = useGitSyncCurrentTarget();
    const currentOrganizationId = useCurrentOrganizationId();
    const initiatedSync =
        currentTarget.target.id === target.id ? currentTarget.initiatedSync : null;

    const organizationId = target.object === 'space' ? target.organization : currentOrganizationId;
    assertIsDefined(organizationId, 'organizationId is required to read Git Sync status');

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', { organizationId })
    );
    const operationSource = getGitSyncOperationSource(target, organizationContent);

    const spaceOperation = useRealtimeChannel(
        {
            channel: 'space-gitsync-operation',
            space: operationSource?.type === 'space' ? operationSource.spaceId : '',
        },
        undefined,
        { skip: operationSource?.type !== 'space' }
    );
    const siteOperation = useRealtimeChannel(
        {
            channel: 'site-gitsync-operation',
            site: operationSource?.type === 'site' ? operationSource.site.id : '',
        },
        undefined,
        { skip: operationSource?.type !== 'site' }
    );
    let operation: GitSyncCurrentOperation | null = spaceOperation;
    if (operationSource?.type === 'site') {
        operation =
            target.object === 'space'
                ? getGitSyncSiteSpaceOperation(siteOperation, target.id)
                : siteOperation;
    }

    const targetStatus = React.useMemo(
        () => getGitSyncCurrentStatus(target, operation, initiatedSync),
        [operation, target, initiatedSync]
    );

    const siteSpaceStatuses = React.useMemo<Record<string, GitSyncCurrentStatus | null>>(() => {
        if (target.object !== 'site') {
            return {};
        }

        // An explicitly initiated site sync is forced and includes every managed space.
        return Object.fromEntries(
            getManagedGitSyncSiteSpaces(target, organizationContent).map((space) => [
                space.id,
                getGitSyncCurrentStatus(
                    space,
                    getGitSyncSiteSpaceOperation(siteOperation, space.id),
                    initiatedSync
                ),
            ])
        );
    }, [initiatedSync, organizationContent, siteOperation, target]);

    if (target.object === 'site') {
        return {
            status: aggregateGitSyncCurrentStatuses([
                targetStatus,
                ...Object.values(siteSpaceStatuses),
            ]),
            site: targetStatus,
            spaces: siteSpaceStatuses,
        };
    }

    return { status: targetStatus };
}

/** Resolve whether a target reads its own channel or a parent site's channel. */
export function getGitSyncOperationSource(
    target: api.Space | api.Site,
    organizationContent: api.OrganizationAllContent
): GitSyncOperationSource | null {
    if (!target.gitSync) {
        return null;
    }

    if (target.object === 'site') {
        return { type: 'site', site: target };
    }

    const parentInstallationId = target.gitSync.parentInstallationId;
    if (!parentInstallationId) {
        return { type: 'space', spaceId: target.id };
    }

    const site = organizationContent.sites.find(
        (item) => item.site.gitSync?.installationId === parentInstallationId
    )?.site;
    return site ? { type: 'site', site } : null;
}

/** Collapse site and child-space statuses using running, failure, then success precedence. */
export function aggregateGitSyncCurrentStatuses(
    statuses: Array<GitSyncCurrentStatus | null>
): GitSyncCurrentStatus | null {
    return (
        statuses.find((status) => status?.state === 'running') ??
        statuses.find(isFailedGitSyncStatus) ??
        statuses.find((status) => status?.state === 'success') ??
        null
    );
}

/** Read one space's latest operation from a site operation channel. */
export function getGitSyncSiteSpaceOperation(
    operation: api.GitSyncSiteOperation | null,
    spaceId: string
): GitSyncCurrentOperation | null {
    const spaceOperation = operation?.spaces?.[spaceId];
    if (!spaceOperation) {
        return null;
    }

    return spaceOperation;
}

function getManagedGitSyncSiteSpaces(
    site: api.Site,
    organizationContent: api.OrganizationAllContent | null
): api.Space[] {
    const siteContent = organizationContent?.sites.find((item) => item.site.id === site.id);
    const parentInstallationId = site.gitSync?.installationId;
    if (!siteContent?.structure || !parentInstallationId) {
        return [];
    }

    return getSiteSpacesFromStructure(siteContent.structure)
        .map((siteSpace) => siteSpace.space)
        .filter((space) => space.gitSync?.parentInstallationId === parentInstallationId);
}

/** Derive the displayed status from realtime data, falling back to the target's operation state. */
export function getGitSyncCurrentStatus(
    target: api.Space | api.Site,
    operation: GitSyncCurrentOperation | null,
    initiatedSync: InitiatedGitSync | null = null
): GitSyncCurrentStatus | null {
    // The channel persists the last operation, so a sync the user just triggered still reports
    // the previous one — possibly a stale `success`/`failure` — until the backend starts the new
    // one. Any operation that started before the trigger is stale: report the requested sync.
    if (
        initiatedSync &&
        (!operation || new Date(operation.startedAt).getTime() < initiatedSync.at)
    ) {
        return {
            state: 'running',
            direction: initiatedSync.direction,
            stage: api.GitSyncOperationStage.RepoFetch,
        };
    }

    const stage = operation?.stage || api.GitSyncOperationStage.RepoFetch;

    if (operation?.state) {
        switch (operation.state) {
            case api.GitSyncOperationState.Running: {
                if (
                    isEndedByTarget(operation.startedAt, target) ||
                    isBeyondOperationTimeout(operation.startedAt, target)
                ) {
                    break;
                }
                return {
                    state: 'running',
                    direction: operation.direction,
                    stage,
                };
            }
            case api.GitSyncOperationState.Failure:
                return {
                    state: 'failure',
                    direction: operation.direction,
                    error: operation.error ?? 'Unknown error',
                    errorAction: operation.errorAction,
                };
            case api.GitSyncOperationState.Success:
                return {
                    state: 'success',
                    direction: operation.direction,
                };
            case api.GitSyncOperationState.Timeout:
                return {
                    state: 'timed-out',
                    direction: operation.direction,
                };
            default:
                assertNever(operation.state);
        }
    }

    return getFallbackGitSyncCurrentStatus(target);
}

/**
 * Derive a static Git Sync status when realtime operation status is unavailable — before the channel
 * has connected.
 *
 * The target's operation carries no stage, so the stage of a running operation is unknown. The API already folds a stalled operation into `timeout`.
 */
export function getFallbackGitSyncCurrentStatus(
    target: api.Space | api.Site
): GitSyncCurrentStatus | null {
    // Import is the assumed direction for a target with no operation to read one from — only the
    // installation status is left to derive a status from, and that path reports success.
    const direction = target.gitSync?.operation?.direction ?? api.GitSyncOperationDirection.Import;

    switch (target.gitSync?.operation?.state) {
        case api.GitSyncOperationState.Running:
            return {
                state: 'running',
                direction,
                stage: api.GitSyncOperationStage.RepoFetch,
            };
        case api.GitSyncOperationState.Failure:
            return {
                state: 'failure',
                direction,
                error: target.gitSync?.operation?.error,
                errorAction: target.gitSync?.operation?.errorAction,
            };
        case api.GitSyncOperationState.Timeout:
            return { state: 'timed-out', direction };
        case api.GitSyncOperationState.Success:
            return { state: 'success', direction };
        default:
            break;
    }

    if (
        target.object === 'site' &&
        target.gitSync?.installationStatus === api.GitSyncInstallationStatus.Active
    ) {
        return { state: 'success', direction };
    }

    return null;
}

/**
 * Whether the operation the realtime channel reports as running has outlived the deadline.
 *
 * The runner executing an operation is terminated after `operationTimeout`, so a channel still
 * reporting `running` past its own `startedAt` plus that duration lost the terminal message it
 * should have published — the operation it describes cannot still be live. The bound is applied to
 * the channel's `startedAt` rather than the target's, because the two can describe different
 * operations: an operation the backend never recorded is reported only by the channel, which is
 * exactly the case {@link isEndedByTarget} cannot resolve.
 *
 * A target reporting no `operationTimeout` applies no bound, leaving the channel trusted as before.
 */
export function isBeyondOperationTimeout(
    startedAt: api.Timestamp,
    target: api.Space | api.Site
): boolean {
    const operationTimeout = target.gitSync?.operationTimeout;

    if (typeof operationTimeout !== 'number') {
        return false;
    }

    return Date.now() - new Date(startedAt).getTime() > operationTimeout;
}

/**
 * Whether the target has already ended the operation the realtime channel started at `startedAt`.
 *
 * The channel persists its last message but publishes nothing for an operation whose worker died, so
 * a `running` it replays is only live if it started after the operation the target ended — including
 * the end the API synthesizes once a stalled operation times out. Both timestamps are recorded by the
 * backend, so they are comparable.
 */
export function isEndedByTarget(startedAt: api.Timestamp, target: api.Space | api.Site): boolean {
    const endedAt = target.gitSync?.operation?.endedAt;

    if (!endedAt) {
        return false;
    }

    return new Date(startedAt).getTime() <= new Date(endedAt).getTime();
}
